import { useState } from 'react';
import { useCreateDeployment, useUpdateDeployment } from '@/features/deployments/hooks/useDeployments';
import { useApiErrorToast } from '@/shared/ui/Toast';
import { PrimaryButton } from '@/shared/ui/PrimaryButton';
import { useModalStyles } from '@/shared/ui/modalStyles';
import { useThemeColors } from '@/shared/theme/useThemeColors';
import { brand, radius, semantic, typography } from '@/shared/theme/tokens';
import { AppSelect } from '@/shared/ui/AppSelect';

const STATUS_OPTIONS = ['active', 'provisioning', 'inactive', 'archived'];

const EMPTY_FORM = { name: '', client: '', location: '', status: 'provisioning', plantType: '' };

export function DeploymentFormModal({ mode, deployment, onClose }) {
  const c = useThemeColors();
  const { overlayStyle, modalStyle } = useModalStyles();
  const [form, setForm] = useState(
    mode === 'edit' && deployment
      ? {
          name: deployment.name,
          client: deployment.client,
          location: deployment.location,
          status: deployment.status,
          plantType: deployment.plantType ?? '',
        }
      : EMPTY_FORM
  );
  const [errors, setErrors] = useState({});

  const toastApiError = useApiErrorToast();
  const { mutateAsync: create, isPending: creating } = useCreateDeployment();
  const { mutateAsync: update, isPending: updating } = useUpdateDeployment();
  const pending = creating || updating;

  function setField(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  }

  function validate() {
    const e = {};
    if (!form.name.trim()) e.name = 'Required';
    if (!form.client.trim()) e.client = 'Required';
    return e;
  }

  async function handleSubmit(ev) {
    ev.preventDefault();
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }

    const autoName = `${form.client} – ${form.location}`.trim();
    const payload = { ...form, name: form.name.trim() || autoName };
    try {
      if (mode === 'create') {
        await create(payload);
      } else {
        await update({ id: deployment.id, payload });
      }
      onClose();
    } catch (err) { toastApiError(err); }
  }

  return (
    <div style={overlayStyle} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={modalStyle}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: 40, height: 40, borderRadius: radius.lg, background: 'linear-gradient(135deg, rgba(32,36,117,0.08), rgba(62,181,231,0.08))', border: `1px solid ${semantic.brandBorderAccent}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span className="material-symbols-outlined" style={{ fontSize: 20, color: brand.navy, fontVariationSettings: "'FILL' 1" }}>hub</span>
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: typography.fontSize['2xl'], fontWeight: typography.fontWeight.bold, color: c.textPrimary, fontFamily: typography.fontFamily.heading }}>
                {mode === 'create' ? 'New Deployment' : 'Edit Deployment'}
              </h3>
              <p style={{ margin: '2px 0 0', fontSize: typography.fontSize.base, color: c.textMuted }}>
                {mode === 'create' ? 'Configure a new client deployment' : 'Update deployment details'}
              </p>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: c.textSubtle }}>
            <span className="material-symbols-outlined" style={{ fontSize: 20 }}>close</span>
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Client */}
          <FormField label="Client Name" error={errors.client} required c={c}>
            <input
              value={form.client}
              onChange={(e) => setField('client', e.target.value)}
              placeholder="e.g. Acme Corp"
              style={fieldInput(!!errors.client, c)}
            />
          </FormField>

          {/* Location */}
          <FormField label="Location / Plant" c={c}>
            <input
              value={form.location}
              onChange={(e) => setField('location', e.target.value)}
              placeholder="e.g. Plant North"
              style={fieldInput(false, c)}
            />
          </FormField>

          {/* Custom Name override */}
          <FormField label="Display Name" error={errors.name} required c={c}>
            <input
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              placeholder="Auto-filled from client + location"
              style={fieldInput(!!errors.name, c)}
            />
          </FormField>

          {/* Status */}
          <FormField label="Status" c={c}>
            <AppSelect
              ariaLabel="status"
              size="sm"
              searchable={false}
              clearable={false}
              options={STATUS_OPTIONS.map((s) => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))}
              value={form.status}
              onChange={(v) => setField('status', v ?? form.status)}
            />
          </FormField>

          {/* Plant Type */}
          <FormField label="Plant Type" c={c}>
            <input
              value={form.plantType}
              onChange={(e) => setField('plantType', e.target.value)}
              placeholder="e.g. Blast Furnace, Cellhouse, Smelter"
              style={fieldInput(false, c)}
            />
          </FormField>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', paddingTop: '8px', borderTop: `1px solid ${c.divider}` }}>
            <PrimaryButton variant="secondary" onClick={onClose} disabled={pending}>Cancel</PrimaryButton>
            <PrimaryButton type="submit" disabled={pending} icon={mode === 'create' ? 'add' : 'check'}>
              {pending ? (mode === 'create' ? 'Creating…' : 'Saving…') : (mode === 'create' ? 'Create Deployment' : 'Save Changes')}
            </PrimaryButton>
          </div>
        </form>
      </div>
    </div>
  );
}

function FormField({ label, children, error, required, c }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
      <label style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold, color: c.textLabel, letterSpacing: typography.letterSpacing.label, textTransform: 'uppercase' }}>
        {label}{required && <span style={{ color: semantic.error, marginLeft: '3px' }}>*</span>}
      </label>
      {children}
      {error && <p style={{ fontSize: typography.fontSize.sm, color: semantic.error, margin: 0, fontWeight: typography.fontWeight.medium }}>{error}</p>}
    </div>
  );
}

function fieldInput(hasError, c) {
  return {
    width: '100%',
    boxSizing: 'border-box',
    background: c.bgSubtle,
    border: `1.5px solid ${hasError ? semantic.error : c.borderInput}`,
    borderRadius: radius.lg,
    padding: '12px 14px',
    minHeight: '44px',
    fontSize: typography.fontSize.lg,
    fontFamily: typography.fontFamily.body,
    color: c.textPrimary,
    outline: 'none',
  };
}
